// @flow
import * as React from 'react';
import styled from 'styled-components';
import {MODE, ShowroomMode} from 'components/Constants';
import { useSessionState, useSessionDispatch } from 'components/Session';
import { IsSmallAgent, IsNotSmallAgent } from 'components/Responsive';
import {OptionButton, Selections, OptionsWrapper, HueSelections} from './Controller';
import { addVREventListener, removeVREventListener, enableWheelCamera, triggerColorCamera, triggerSeatCamera, triggerMoodlampCamera, triggerCustomizationCamera } from 'utils/vrUtils';

const CAR = {
  NONE: '',
  INFO: 'info',
  COLOR: 'color',
  WHEEL: 'wheel',
  BG: 'bg',
  LAMP: 'lamp',
  DOOR: 'door',
  IINFO: 'info',
  SEAT: 'seat',
  ISEAT: 'iseat',
  AMBIENT: 'ambient',
  POSITION: 'position',
  OPTION: 'option',
  SIGNALLAMP: 'signal_lamp',
  MODE: 'mode'
};

const carOptions = [[CAR.COLOR, CAR.WHEEL, CAR.SEAT, CAR.OPTION, CAR.BG, CAR.AMBIENT, CAR.LAMP, CAR.SIGNALLAMP, CAR.DOOR, CAR.MODE], [CAR.POSITION, CAR.MODE]];
const carOptionsSub = [[CAR.COLOR, CAR.WHEEL, CAR.SEAT, CAR.OPTION, CAR.BG, CAR.AMBIENT], [CAR.POSITION]];

type PropsControllerOptions = {
  mode: number,
  hidden: boolean,
  onInfo: (string) => void,
  onChangeMode: (number) => void,
  selectedInfo: boolean,
}

export default function CarOptions(props: PropsControllerOptions): React.Element<any> {
  const sessionState = useSessionState();
  const sessionDispatch = useSessionDispatch();

  const [selectedOption, setSelectedOption] = React.useState(CAR.NONE);
  const prevSelectedOption = React.useRef(CAR.NONE);
  const [toggleOptions, setToggleOptions] = React.useState([]);
  const options = React.useMemo(() => props.mode !== MODE.NONE ? carOptions[props.mode] : undefined, [props.mode]);

  const handleToggleOption = React.useCallback((option: string) => {
    if (toggleOptions.includes(option)) {
      const toggleOptionsNext = toggleOptions.filter(value => value !== option);
      setToggleOptions(toggleOptionsNext);
      sessionDispatch({type: 'SELECT_OPTION_DATA', data: false, option: option});
    }
    else {
      const toggleOptionsNext = toggleOptions.concat([option]);
      setToggleOptions(toggleOptionsNext);
      sessionDispatch({type: 'SELECT_OPTION_DATA', data: true, option: option});
    }

    }, [sessionDispatch, toggleOptions]);

  const handleOption = React.useCallback((option: string) => {
    //console.log("handleOption:", option, selectedOption);

    props.onInfo(option === CAR.INFO && selectedOption !== CAR.INFO ? (props.mode === MODE.EXTERIOR ? ShowroomMode.EXTERIOR : ShowroomMode.INTERIOR) : '');
    prevSelectedOption.current = selectedOption;

    if (option === CAR.LAMP || option === CAR.DOOR || option === CAR.SIGNALLAMP) {
      handleToggleOption(option);
      return;
    }

    if (option === CAR.MODE) {
      props.onChangeMode(props.mode === MODE.INTERIOR ? MODE.EXTERIOR : MODE.INTERIOR);
      return;
    }

    if (selectedOption === option) {
      if (selectedOption === CAR.WHEEL) {
        enableWheelCamera(false);
      }
      // toggle (OFF)
      setSelectedOption(CAR.NONE);
    }
    else {
      // ON
      setSelectedOption(option);
      if (option === CAR.WHEEL) {
        enableWheelCamera(true);
      } else {
        enableWheelCamera(false);

        if (option === CAR.COLOR) {
          triggerColorCamera();
        }
        else if (option === CAR.SEAT) {
          triggerSeatCamera();
        }
        else if (option === CAR.AMBIENT) {
          triggerMoodlampCamera();
        }
        else if (option === CAR.OPTION) {
          triggerCustomizationCamera();
        }
      }


    }
  }, [selectedOption, props, handleToggleOption]);


  const handleCarLamp = React.useCallback((data: bool) => {
    if ((data && !toggleOptions.includes(CAR.LAMP)) || (!data && toggleOptions.includes(CAR.LAMP))) {
      handleToggleOption(CAR.LAMP);
    }
  }, [toggleOptions, handleToggleOption]);

  const handleCarDoor = React.useCallback((data: bool) => {
    if ((data && !toggleOptions.includes(CAR.DOOR)) || (!data && toggleOptions.includes(CAR.DOOR))) {
      handleToggleOption(CAR.DOOR);
    }
  }, [toggleOptions, handleToggleOption]);

  React.useEffect(() => {
    if (!props.selectedInfo && selectedOption === CAR.INFO) {
      handleOption(CAR.NONE);
    }
  }, [props.selectedInfo, selectedOption, handleOption]);

  React.useEffect(() => {
    setSelectedOption(CAR.NONE);
    prevSelectedOption.current = CAR.NONE;
    //console.log("calling CAR.NONE");
  }, [props.mode]);

  React.useEffect(() => {
    addVREventListener("car:lamp", handleCarLamp);
    addVREventListener("car:door", handleCarDoor);
    return () => {
      removeVREventListener("car:lamp", handleCarLamp);
      removeVREventListener("car:door", handleCarDoor)
    }
  });

  return (
    <>
      <SelctionsGroup dataHidden={props.hidden}>
        {props.mode !== MODE.NONE && carOptionsSub[props.mode].map((value, index) =>
          <OptionSelections key={value} prefix={value} dataHidden={props.hidden} selectedOption={selectedOption} prevSelectedOption={prevSelectedOption.current} modelIndex={sessionState.modelIndex} dataMode={props.mode} />
        )}
        {props.mode !== MODE.NONE && (selectedOption === CAR.AMBIENT || (selectedOption === CAR.NONE && prevSelectedOption.current === CAR.AMBIENT)) && <HueSelections closing={selectedOption === CAR.NONE} dataClosingDirection={1} />}
      </SelctionsGroup>
      {/* <IsMobileAgent>
        <SelectedText className="option-need-safearea" dataHidden={props.hidden}>
        {selectedOptionCode !== "" && <LocalText textKey="options_name_format" args={[`$options_${selectedOptionCode}_name`]} />}
        </SelectedText>
      </IsMobileAgent> */}
      <OptionsContainer dataHidden={props.hidden} isMultiRow={(options?.length ?? 0) > 5}>
        <OptionsWrapper dataHidden={props.hidden} isMultiRow={(options?.length ?? 0) > 5}>
        <IsNotSmallAgent>
          <div>
            {options?.map(value => <OptionButton key={value} onClick={() => handleOption(value)} prefix={props.mode === MODE.EXTERIOR ? "exterior" : "interior"} code={value} active={selectedOption === value || toggleOptions.includes(value)} />)}
          </div>
        </IsNotSmallAgent>
        <IsSmallAgent>
          <div>
          {options?.slice(0, 5).map(value => <OptionButton key={value} onClick={() => handleOption(value)} prefix={props.mode === MODE.EXTERIOR ? "exterior" : "interior"} code={value} active={selectedOption === value || toggleOptions.includes(value)} />)}
          </div>
          <DivSpace />
          <div>
          {options?.slice(5).map(value => <OptionButton key={value} onClick={() => handleOption(value)} prefix={props.mode === MODE.EXTERIOR ? "exterior" : "interior"} code={value} active={selectedOption === value || toggleOptions.includes(value)} />)}
          </div>
          </IsSmallAgent>
        </OptionsWrapper>
      </OptionsContainer>
    </>
  );
}

type PropsOptionSelections = {
  prefix: string,
  dataHidden: boolean,
  selectedOption: string,
  prevSelectedOption: string,
  modelIndex: number,
  dataMode: number,
}

function OptionSelections(props: PropsOptionSelections) {
  //console.log(props);
  const modeIndex = React.useMemo(() => props.dataMode ?? 0, [props]);
  const currPoint = React.useMemo(() => props.selectedOption === CAR.NONE ? 100 : carOptions[modeIndex].findIndex(v => v === props.selectedOption) + 1, [props, modeIndex]);
  const prevPoint = React.useMemo(() => props.prevSelectedOption === CAR.NONE ? 0 : carOptions[modeIndex].findIndex(v => v === props.prevSelectedOption) + 1, [props, modeIndex]);
  const closingDir = React.useMemo(() => currPoint - prevPoint, [currPoint, prevPoint]);
  
  if (props.selectedOption === props.prefix || (props.selectedOption === CAR.NONE && props.prevSelectedOption === props.prefix)) {
    return <Selections prefix={props.prefix} closing={props.selectedOption === CAR.NONE} closingDirection={closingDir} dataHidden={props.dataHidden} />
  }
  return null;
}

const SelctionsGroup = styled.div`
  display: flex;
  justify-content: center;

  opacity: ${(props: any) => props.dataHidden ? 0 : undefined};
  transition: opacity ${(props: any) => props.theme.defaults.transitionDuration}s;

  * button {
    pointer-events: ${(props: any) => props.dataHidden ? 'none' : undefined};
  }
`

const OptionsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const DivSpace = styled.div`
  height: ${(props: any) => props.theme.layout.sizeL ? undefined : '6px'};
`